import { FC } from "react";
import { useHotelBookingContext } from "../context/HotelBookingContext";
import { MainSearch } from "../../../Components";
import HeroGallery from "../components/HeroGallery";
import HotelHeader from "../components/HotelHeader";
import BookingTabs from "../components/BookingTabs";
import PropertyPolicies from "../components/PropertyPolicies";
import PopularAmenities from "../components/PopularAmenities";
import AccommodationsList from "../components/AccommodationsList";
import PremiumAmenities from "../components/PremiumAmenities";
import LocationInfo from "../components/LocationInfo";
import SummarySidebar from "../components/SummarySidebar";
import { HotelBookingSharedProps } from "../HotelBooking";
import CountdownTimerSlider from "../../../Components/CountdownTimerSlider/CountdownTimerSlider";

const HotelBookingWeb: FC<HotelBookingSharedProps> = ({
  sections,
  overviewRef,
  popularAmenitiesRef,
  roomsRef,
  amenitiesRef,
  locationRef,
  extraGuestCharges,
  handleProceed,
}) => {
  const { hotel, searchObject, selectedRoomIndex, setSelectedRoomIndex, selectedBookingCode, numberOfNights, pointsFactor } =
    useHotelBookingContext();

  return (
    <div className="flex flex-col bg-background-default">
      <div className="border-b border-border-default p-8 shadow-md">
        <MainSearch initialValues={searchObject} />
      </div>
      <div className="sticky top-0 z-20">
        <CountdownTimerSlider />
      </div>

      <div className="mx-auto mt-8 w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <HeroGallery
            images={hotel.images}
            fallbackImage={hotel.image}
            hotelName={hotel.hotelName}
            subtitle={hotel.address ?? undefined}
          />
        </div>

        <div className="flex scroll-mt-24 items-start gap-8">
          <div className="flex min-w-0 flex-1 flex-col gap-8 pb-16">
            <div data-section-key="overview" ref={overviewRef}>
              <HotelHeader
                hotelName={hotel.hotelName}
                hotelRating={hotel.hotelRating}
                address={hotel.address}
                hotelWebsiteUrl={hotel.hotelWebsiteUrl}
                description={hotel.description}
                latitude={hotel.latitude}
                longitude={hotel.longitude}
              />
            </div>

            <div className="sticky top-5 z-10 scroll-mt-24">
              <BookingTabs sections={sections} />
            </div>

            <div ref={popularAmenitiesRef} data-section-key="popularAmenities" className="scroll-mt-24">
              <PopularAmenities categorizedFacilities={hotel.categorizedFacilities}/>
            </div>

            <div ref={roomsRef} data-section-key="rooms" className="scroll-mt-24">
              <AccommodationsList
                rooms={hotel.rooms}
                currency={hotel.currency}
                originalCurrency={hotel.originalCurrency}
                numberOfNights={numberOfNights}
                selectedIndex={selectedRoomIndex}
                onSelect={setSelectedRoomIndex}
                onProceed={handleProceed}
                pointsFactor={pointsFactor}
                totalAdults={searchObject?.rooms?.reduce((sum, r) => sum + (r.adults ?? 0), 0) ?? 0}
                totalChildren={searchObject?.rooms?.reduce((sum, r) => sum + (r.children ?? 0), 0) ?? 0}
              />
            </div>

            <PropertyPolicies
              checkInTime={hotel.checkInTime}
              checkOutTime={hotel.checkOutTime}
              phoneNumber={hotel.phoneNumber}
              extraGuestCharges={extraGuestCharges}
              currency={hotel.currency}
            />

            <div ref={amenitiesRef} data-section-key="amenities" className="scroll-mt-24">
              <PremiumAmenities facilities={hotel.categorizedFacilities} />
            </div>

            <div ref={locationRef} data-section-key="location" className="scroll-mt-24">
              <LocationInfo attractions={hotel.attractions} latitude={hotel.latitude} longitude={hotel.longitude} />
            </div>

            <input type="hidden" data-testid="booking-code" value={selectedBookingCode ?? ""} />
          </div>

          <div className="sticky top-0 hidden h-fit w-80 shrink-0 lg:block">
            <div className="flex flex-col gap-2">
              <SummarySidebar searchObject={searchObject} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HotelBookingWeb;
